<template>
  <div class="profile">
    <p class="profile__subtitle">
      {{ $t('auth.profile.subtitle') }}
    </p>

    <div class="row">
      <!-- Identidade + foto -->
      <div class="col-xl-4 col-lg-5">
        <div class="card profile__identity h-100">
          <div class="profile__cover" />
          <div class="card-body profile__identity-body">
            <button
              type="button"
              class="profile__avatar-btn"
              :title="$t('auth.profile.changePhoto')"
              @click="$refs.photoInput.click()"
            >
              <b-avatar
                :src="avatarSrc"
                :text="initials"
                rounded="lg"
                size="150px"
                variant="light-primary"
                class="profile__avatar"
              />
              <span class="profile__avatar-overlay">
                <feather-icon
                  icon="CameraIcon"
                  size="22"
                />
                <span>{{ $t('auth.profile.changePhoto') }}</span>
              </span>
            </button>
            <input
              ref="photoInput"
              type="file"
              class="d-none"
              accept="image/jpeg,image/png,image/webp"
              @change="onPhotoChosen"
            >

            <span
              v-if="photoFile"
              class="badge badge-light-primary profile__new-photo mt-1"
            >
              {{ $t('auth.profile.newPhoto') }}
            </span>

            <h4 class="profile__name mt-1 mb-25">
              {{ form.name || user.name }}
            </h4>
            <div class="text-muted profile__login">
              @{{ user.login }}
            </div>

            <ul class="profile__facts">
              <li>
                <feather-icon
                  icon="AtSignIcon"
                  size="15"
                />
                <span class="profile__fact-label">{{ $t('auth.profile.loginField') }}</span>
                <strong>{{ user.login }}</strong>
              </li>
              <li v-if="user.professional_cbo">
                <feather-icon
                  icon="BriefcaseIcon"
                  size="15"
                />
                <span class="profile__fact-label">{{ $t('auth.profile.cboField') }}</span>
                <span class="profile__fact-value">
                  <strong>{{ user.professional_cbo.name }}</strong>
                  <small
                    v-if="user.professional_cbo.code"
                    class="text-muted"
                  >{{ user.professional_cbo.code }}</small>
                </span>
              </li>
              <li v-if="roles.length">
                <feather-icon
                  icon="ShieldIcon"
                  size="15"
                />
                <span class="profile__fact-label">{{ $tc('auth.profile.roleField', roles.length) }}</span>
                <span class="profile__fact-value">
                  <span
                    v-for="r in roles"
                    :key="r"
                    class="profile__role"
                  >{{ r }}</span>
                </span>
              </li>
              <li v-if="nodeName">
                <feather-icon
                  icon="GitBranchIcon"
                  size="15"
                />
                <span class="profile__fact-label">{{ $t('auth.profile.nodeField') }}</span>
                <strong>{{ nodeName }}</strong>
              </li>
            </ul>

            <!-- Rodapé do cartão: cola embaixo para o cartão acompanhar a altura das duas colunas da direita. -->
            <div class="profile__photo-box">
              <p class="profile__photo-hint mb-0">
                {{ $t('auth.profile.photoHint') }}
              </p>
              <div
                v-if="photoError"
                class="text-danger small mt-50"
              >
                {{ photoError }}
              </div>
              <button
                v-if="photoFile"
                type="button"
                class="btn btn-sm btn-flat-secondary mt-50"
                @click="discardPhoto"
              >
                <feather-icon
                  icon="XIcon"
                  size="13"
                  class="mr-25"
                />
                {{ $t('auth.profile.discardPhoto') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-8 col-lg-7">
        <!-- Dados pessoais -->
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <feather-icon
                icon="UserIcon"
                size="16"
                class="mr-50 text-primary"
              />
              {{ $t('auth.profile.personalTitle') }}
            </h5>
          </div>
          <div class="card-body">
            <div class="form-row">
              <div class="form-group col-md-7">
                <label for="profile-name">
                  <span class="required">*</span> {{ $t('auth.profile.nameField') }}
                </label>
                <input
                  id="profile-name"
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': showNameError }"
                  maxlength="191"
                  autocomplete="name"
                  @blur="nameTouched = true"
                >
                <div class="invalid-feedback">
                  {{ $t('auth.profile.nameRequired') }}
                </div>
              </div>
              <div class="form-group col-md-5">
                <label for="profile-login">{{ $t('auth.profile.loginField') }}</label>
                <div class="input-group">
                  <input
                    id="profile-login"
                    :value="user.login"
                    type="text"
                    class="form-control"
                    disabled
                  >
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <feather-icon
                        icon="LockIcon"
                        size="14"
                      />
                    </span>
                  </div>
                </div>
                <small class="text-muted">{{ $t('auth.profile.loginHint') }}</small>
              </div>
            </div>

            <div class="d-flex justify-content-end profile__actions">
              <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="saving || !dirty"
                @click="resetProfile"
              >
                {{ $t('auth.profile.discardChanges') }}
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="saving || !dirty || !nameValid"
                @click="saveProfile"
              >
                <b-spinner
                  v-if="saving"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  size="14"
                  class="mr-50"
                />
                {{ $t('auth.profile.saveChanges') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Segurança -->
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <feather-icon
                icon="ShieldIcon"
                size="16"
                class="mr-50 text-primary"
              />
              {{ $t('auth.profile.securityTitle') }}
            </h5>
          </div>
          <div class="card-body">
            <p class="text-muted small">
              {{ $t('auth.profile.securityHint') }}
            </p>

            <!-- Campos-isca: evitam o preenchimento automático do navegador no formulário de perfil -->
            <input
              type="text"
              class="d-none"
              autocomplete="username"
              :value="user.login"
              tabindex="-1"
            >

            <div class="form-group">
              <label for="profile-current">{{ $t('auth.profile.currentPassword') }}</label>
              <div class="input-group">
                <input
                  id="profile-current"
                  v-model="pwd.current"
                  :type="show.current ? 'text' : 'password'"
                  class="form-control"
                  :class="{ 'is-invalid': pwdErrors.current_password }"
                  autocomplete="current-password"
                >
                <div class="input-group-append">
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :title="show.current ? $t('auth.profile.hidePassword') : $t('auth.profile.showPassword')"
                    @click="show.current = !show.current"
                  >
                    <feather-icon
                      :icon="show.current ? 'EyeOffIcon' : 'EyeIcon'"
                      size="15"
                    />
                  </button>
                </div>
                <div class="invalid-feedback">
                  {{ pwdErrors.current_password }}
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group col-md-6">
                <label for="profile-new">{{ $t('auth.profile.newPassword') }}</label>
                <div class="input-group">
                  <input
                    id="profile-new"
                    v-model="pwd.next"
                    :type="show.next ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': pwdErrors.password }"
                    autocomplete="new-password"
                  >
                  <div class="input-group-append">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :title="show.next ? $t('auth.profile.hidePassword') : $t('auth.profile.showPassword')"
                      @click="show.next = !show.next"
                    >
                      <feather-icon
                        :icon="show.next ? 'EyeOffIcon' : 'EyeIcon'"
                        size="15"
                      />
                    </button>
                  </div>
                  <div class="invalid-feedback">
                    {{ pwdErrors.password }}
                  </div>
                </div>
              </div>
              <div class="form-group col-md-6">
                <label for="profile-confirm">{{ $t('auth.profile.confirmPassword') }}</label>
                <div class="input-group">
                  <input
                    id="profile-confirm"
                    v-model="pwd.confirm"
                    :type="show.confirm ? 'text' : 'password'"
                    class="form-control"
                    autocomplete="new-password"
                    @keyup.enter="changePassword"
                  >
                  <div class="input-group-append">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :title="show.confirm ? $t('auth.profile.hidePassword') : $t('auth.profile.showPassword')"
                      @click="show.confirm = !show.confirm"
                    >
                      <feather-icon
                        :icon="show.confirm ? 'EyeOffIcon' : 'EyeIcon'"
                        size="15"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <ul class="profile__rules">
              <li
                v-for="rule in passwordRules"
                :key="rule.key"
                :class="{ 'is-ok': rule.ok }"
              >
                <feather-icon
                  :icon="rule.ok ? 'CheckCircleIcon' : 'CircleIcon'"
                  size="14"
                />
                {{ rule.label }}
              </li>
            </ul>

            <div class="d-flex justify-content-end profile__actions">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="changing || !passwordValid"
                @click="changePassword"
              >
                <b-spinner
                  v-if="changing"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="KeyIcon"
                  size="14"
                  class="mr-50"
                />
                {{ $t('auth.profile.changePassword') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

const MIN_PASSWORD = 8
const MAX_PHOTO_BYTES = 2 * 1024 * 1024
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp']

export default {
  name: 'Profile',

  mixins: [ApiErrorMixin],

  data() {
    return {
      form: { name: (this.$store.state.user && this.$store.state.user.name) || '' },
      nameTouched: false,
      saving: false,
      photoFile: null,
      photoPreview: null,
      photoError: '',
      pwd: { current: '', next: '', confirm: '' },
      show: { current: false, next: false, confirm: false },
      pwdErrors: {},
      changing: false,
    }
  },

  computed: {
    user() {
      return this.$store.state.user || {}
    },
    userName() {
      return this.user.name
    },
    roles() {
      return Array.isArray(this.user.roles) ? this.user.roles : []
    },
    nodeName() {
      const node = this.$store.state.selected_node

      return node && node.name ? node.name : ''
    },
    avatarSrc() {
      return this.photoPreview || this.user.photo || null
    },
    initials() {
      return String(this.form.name || this.user.name || '?')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(p => p[0].toUpperCase())
        .join('')
    },
    nameValid() {
      return this.form.name.trim().length > 0
    },
    showNameError() {
      return this.nameTouched && !this.nameValid
    },
    // Há algo a salvar: nome diferente do atual ou uma foto nova escolhida.
    dirty() {
      return this.form.name.trim() !== String(this.user.name || '').trim() || Boolean(this.photoFile)
    },
    passwordRules() {
      return [
        { key: 'length', ok: this.pwd.next.length >= MIN_PASSWORD, label: this.$t('auth.profile.ruleLength', { min: MIN_PASSWORD }) },
        { key: 'match', ok: Boolean(this.pwd.next) && this.pwd.next === this.pwd.confirm, label: this.$t('auth.profile.ruleMatch') },
        { key: 'different', ok: Boolean(this.pwd.next) && this.pwd.next !== this.pwd.current, label: this.$t('auth.profile.ruleDifferent') },
      ]
    },
    passwordValid() {
      return Boolean(this.pwd.current) && this.passwordRules.every(r => r.ok)
    },
  },

  watch: {
    // O usuário da store pode chegar depois da tela (recarga da página): preenche o nome quando vier,
    // sem pisar no que o operador já digitou.
    userName(name) {
      if (name && !this.form.name) this.form.name = name
    },
  },

  beforeDestroy() {
    this.revokePreview()
  },

  methods: {
    onPhotoChosen(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return

      this.photoError = ''

      if (!PHOTO_TYPES.includes(file.type)) {
        this.photoError = this.$t('auth.profile.photoInvalidType')

        return
      }
      if (file.size > MAX_PHOTO_BYTES) {
        this.photoError = this.$t('auth.profile.photoTooLarge')

        return
      }

      this.revokePreview()
      this.photoFile = file
      this.photoPreview = URL.createObjectURL(file)
    },
    discardPhoto() {
      this.revokePreview()
      this.photoFile = null
      this.photoPreview = null
      this.photoError = ''
    },
    revokePreview() {
      if (this.photoPreview) URL.revokeObjectURL(this.photoPreview)
    },
    resetProfile() {
      this.discardPhoto()
      this.form.name = this.user.name || ''
      this.nameTouched = false
    },

    async saveProfile() {
      this.nameTouched = true
      if (!this.nameValid || this.saving) return

      this.saving = true
      try {
        const fd = new FormData()
        fd.append('name', this.form.name.trim())
        if (this.photoFile) fd.append('photo', this.photoFile)

        await this.$http.post(`${this.$store.state.api}profile`, fd)

        // Recarrega o usuário da store: o nome e a foto do menu acompanham, sem F5.
        this.$store.commit('profileUpdate')
        this.discardPhoto()
        this.$message(this.$t('auth.messages.successTitle'), this.$t('auth.profile.saved'), 'success')
      } catch (e) {
        this.$message(this.$t('auth.profile.errorTitle'), this.apiErrorText(e, this.$t('auth.profile.saveError')), 'error')
      } finally {
        this.saving = false
      }
    },

    async changePassword() {
      if (!this.passwordValid || this.changing) return

      this.changing = true
      this.pwdErrors = {}
      try {
        await this.$http.post(`${this.$store.state.api}profile/password`, {
          current_password: this.pwd.current,
          password: this.pwd.next,
          password_confirmation: this.pwd.confirm,
        })

        this.pwd = { current: '', next: '', confirm: '' }
        this.show = { current: false, next: false, confirm: false }
        this.$message(this.$t('auth.messages.successTitle'), this.$t('auth.profile.passwordChanged'), 'success')
      } catch (e) {
        const data = (e.response && e.response.data) || {}
        // Validação do servidor vem como { campo: ["mensagem"] }: mostra no campo certo (ex.: senha atual incorreta).
        const firstOf = field => (Array.isArray(data[field]) ? data[field][0] : '')
        this.pwdErrors = { current_password: firstOf('current_password'), password: firstOf('password') }

        if (!this.pwdErrors.current_password && !this.pwdErrors.password) {
          this.$message(this.$t('auth.profile.errorTitle'), this.apiErrorText(e, this.$t('auth.profile.passwordError')), 'error')
        }
      } finally {
        this.changing = false
      }
    },
  },
}
</script>

<style scoped>
.profile__subtitle {
  margin: -6px 0 18px;
  color: #6e6b7b;
}

.profile__identity {
  overflow: hidden;
}

/* Faixa de capa: dá peso ao cartão e a foto sobe por cima dela. */
.profile__cover {
  height: 96px;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
}

.profile__identity-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.profile__avatar-btn {
  position: relative;
  display: inline-block;
  margin-top: -86px;
  padding: 0;
  border: 4px solid #fff;
  border-radius: 16px;
  background: #fff;
  cursor: pointer;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(34, 41, 47, 0.18);
}

.profile__avatar {
  display: block;
}

/* Câmera por cima da foto: aparece ao passar o mouse (e fica sempre visível em tela de toque). */
.profile__avatar-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(34, 41, 47, 0.55);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.15s;
}

.profile__avatar-btn:hover .profile__avatar-overlay,
.profile__avatar-btn:focus-visible .profile__avatar-overlay {
  opacity: 1;
}

@media (hover: none) {
  .profile__avatar-overlay { opacity: 1; background: linear-gradient(transparent 55%, rgba(34, 41, 47, 0.65)); justify-content: flex-end; padding-bottom: 8px; }
}

.profile__name {
  font-weight: 600;
}

.profile__login {
  font-size: 13px;
}

.profile__facts {
  width: 100%;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
  text-align: left;
}

.profile__facts li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 0;
  border-top: 1px solid #ebe9f1;
  font-size: 13px;
}

.profile__facts li > svg {
  flex: none;
  color: #7367f0;
}

.profile__fact-label {
  flex: none;
  min-width: 78px;
  color: #82868b;
}

.profile__fact-value {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  min-width: 0;
}

.profile__role {
  padding: 2px 10px;
  border-radius: 12px;
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0;
  font-size: 12px;
  font-weight: 600;
}

.profile__facts strong {
  min-width: 0;
  overflow-wrap: anywhere;
  font-weight: 600;
}

.profile__photo-box {
  width: 100%;
  margin-top: auto;
  padding: 12px;
  border: 1px dashed #d8d6de;
  border-radius: 8px;
  background: rgba(115, 103, 240, 0.04);
  text-align: center;
}

.profile__photo-hint {
  color: #82868b;
  font-size: 12px;
}

.profile__actions {
  gap: 8px;
  margin-top: 4px;
}

.profile__rules {
  margin: 4px 0 14px;
  padding: 0;
  list-style: none;
  font-size: 13px;
  color: #82868b;
}

.profile__rules li {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
}

.profile__rules li.is-ok {
  color: #28c76f;
}

.required {
  color: #ea5455;
}

.dark-layout .profile__subtitle,
.dark-layout .profile__photo-hint {
  color: #b4b7bd;
}

.dark-layout .profile__avatar-btn {
  border-color: #283046;
  background: #283046;
}

.dark-layout .profile__facts li,
.dark-layout .profile__photo-box {
  border-color: #3b4253;
}
</style>
